import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import {
  Inbox,
  Users,
  BarChart3,
  MessageSquare,
  MessagesSquare,
  Headset,
  Tag,
  Brain,
  BookOpen,
  Mail,
  ScrollText,
  FolderUp,
  Settings,
  Bell,
  LogOut,
} from "lucide-react";
import { api } from "@/lib/api";
import { BRAND } from "@/brand";
import { alertForEvent } from "@/lib/adminNotify";
import AdminInstallButton from "./AdminInstallButton";
import AdminInstallNudge from "./AdminInstallNudge";

const TABS = [
  { to: "/admin/submissions", label: "Submissions", icon: Inbox },
  { to: "/admin/customers", label: "User Accounts", icon: Users },
  { to: "/admin/live-chat", label: "Live Chat", icon: Headset, badge: "live" },
  { to: "/admin/notifications", label: "Notifications", icon: Bell },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/payouts", label: "Support", icon: MessageSquare },
  { to: "/admin/chat", label: "All Chats", icon: MessagesSquare },
  { to: "/admin/blog", label: "Blog", icon: Tag },
  { to: "/admin/ai-learning", label: "AI Learning", icon: Brain },
  { to: "/admin/kb", label: "Knowledge Base", icon: BookOpen },
  { to: "/admin/support-inbox", label: "Support Inbox", icon: Mail, badge: "support" },
  { to: "/admin/vendor-reports", label: "Vendor Reports", icon: FolderUp },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const [me, setMe] = useState(null);
  const [liveChatBadge, setLiveChatBadge] = useState(0);
  const [supportBadge, setSupportBadge] = useState({ pending: 0, pending_red: 0 });
  const nav = useNavigate();
  useEffect(() => {
    const tok = localStorage.getItem("gf_admin_token");
    if (!tok) {
      nav("/admin/login");
      return;
    }
    api.get("/admin/me").then((r) => setMe(r.data)).catch(() => {
      localStorage.removeItem("gf_admin_token");
      nav("/admin/login");
    });
  }, [nav]);

  // Poll for unread Live Chat sessions every 8s — drives the red pulse badge
  useEffect(() => {
    if (!me) return undefined;
    let cancelled = false;
    let prevCount = 0;
    let prevManagerRequested = 0;
    let firstRun = true;
    const refresh = async () => {
      try {
        const r = await api.get("/admin/live-chat/sessions");
        if (cancelled) return;
        const open = (r.data || []).filter((s) => !s.closed_at);
        const count = open.filter(
          (s) => s.manager_requested || (s.unread_count || 0) > 0,
        ).length;
        const managerRequested = open.filter((s) => s.manager_requested && !s.claimed_by).length;
        setLiveChatBadge(count);
        if (!firstRun && managerRequested > prevManagerRequested) {
          alertForEvent(
            "new_chat",
            "💬 New manager request",
            "A customer needs a human in the Live Chat inbox.",
            () => window.location.assign("/admin/live-chat"),
          );
        }
        prevCount = count;
        prevManagerRequested = managerRequested;
        firstRun = false;
      } catch {
        /* silent */
      }
    };
    refresh();
    const id = setInterval(refresh, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, [me]);

  // Poll for pending Support Inbox emails every 30s — drives the badge counter
  useEffect(() => {
    if (!me) return undefined;
    let cancelled = false;
    const refresh = async () => {
      try {
        const r = await api.get("/admin/support/counts");
        if (!cancelled) setSupportBadge(r.data || { pending: 0, pending_red: 0 });
      } catch {
        /* silent */
      }
    };
    refresh();
    const id = setInterval(refresh, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [me]);

  const logout = () => {
    localStorage.removeItem("gf_admin_token");
    nav("/admin/login");
  };

  if (!me) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-layout">
      <AdminInstallNudge />

      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="px-4 sm:px-6 md:px-10 py-3 md:py-4 flex items-center justify-between gap-3">
          <Link to="/admin" className="flex items-center gap-2 md:gap-3 min-w-0" data-testid="admin-brand">
            <img
              src="/logo-mark.svg"
              alt="GC SPARK logo"
              className="w-9 h-9 md:w-12 md:h-12 flex-shrink-0"
            />
            <div className="min-w-0">
              <div className="text-sm md:text-lg font-bold text-slate-900 leading-tight truncate">Admin</div>
              <div className="text-[11px] md:text-xs text-slate-500 hidden sm:block truncate">Manage submissions efficiently</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <AdminInstallButton />
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 sm:gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 h-11 px-3 rounded-lg hover:bg-slate-100"
              aria-label="Logout"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Tabs — horizontal scroll on mobile, no wrap */}
      <div className="px-4 sm:px-6 md:px-10 pt-3 md:pt-6 bg-slate-50 sticky top-[60px] md:top-[76px] z-20 border-b border-slate-100 md:border-b-0">
        <nav
          className="flex gap-2 md:gap-3 overflow-x-auto pb-3 md:pb-0 -mx-4 px-4 sm:-mx-0 sm:px-0 scrollbar-none"
          data-testid="admin-tabs"
        >
          {TABS.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={`admin-tab-${t.to.split("/").pop()}`}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 px-3 sm:px-4 h-11 rounded-xl text-xs sm:text-sm font-semibold transition-all flex-shrink-0 whitespace-nowrap ${
                  isActive
                    ? "bg-slate-900 text-white shadow-md ring-2 ring-sky-400/40"
                    : "bg-white border border-slate-200 text-slate-700 hover:border-slate-300"
                }`
              }
            >
              <t.icon className="w-4 h-4 flex-shrink-0" />
              <span>{t.label}</span>
              {t.badge === "live" && liveChatBadge > 0 && (
                <span
                  data-testid="admin-tab-live-chat-badge"
                  className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-rose-500 text-white animate-pulse"
                >
                  {liveChatBadge}
                </span>
              )}
              {t.badge === "support" && supportBadge.pending > 0 && (
                <span
                  data-testid="admin-tab-support-inbox-badge"
                  title={`${supportBadge.pending} awaiting review${supportBadge.pending_red ? ` · ${supportBadge.pending_red} need a human` : ""}`}
                  className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold text-white ${
                    supportBadge.pending_red > 0 ? "bg-rose-500 animate-pulse" : "bg-slate-500"
                  }`}
                >
                  {supportBadge.pending}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="h-1 mt-3 md:mt-5 bg-gradient-to-r from-sky-500 via-violet-500 to-rose-500 rounded-full hidden md:block" />
      </div>

      {/* Page content */}
      <main className="px-2 sm:px-3 md:px-4 pb-20">
        <Outlet />
      </main>
    </div>
  );
}

// keep brand var referenced so it doesn't get tree-shaken in dev
void BRAND;
